<template>
    <BaseDialog v-model:open="open" :title="t('profile.collaboration.shareDialog.title')" :description="sharingDescription" :reserve-mobile-nav-space="!isShareBack">
        <BaseCard theme="muted" padding="lg" class="w-full">
            <div class="flex flex-col items-center justify-center gap-3 text-center">
                <i v-if="isPrivateAccessLevel" class="bi bi-lock text-2xl text-ink-muted" aria-hidden="true"></i>
                <OrganizationIcon v-else-if="isOrgAccessLevel" class="h-6 w-6 text-ink-muted" aria-hidden="true" />
                <i v-else class="bi bi-people text-2xl text-ink-muted" aria-hidden="true"></i>
                <div>
                    <p class="body-small-bold text-ink">{{ statusTitle }}</p>
                    <p class="body-small text-ink-muted">{{ statusBody }}</p>
                </div>
            </div>
        </BaseCard>

        <div class="flex items-center justify-between gap-4" data-testid="general-access-section">
            <div class="flex items-center gap-2">
                <span class="body-small-semibold text-ink">{{ t("profile.collaboration.shareDialog.generalAccess") }}</span>
                <LoadingSpinner v-if="isAccessChanging" class="text-ink-muted w-4 h-4" />
            </div>
            <div class="shrink-0">
                <BaseSelect
                    v-model="localAccess"
                    :disabled="isButtonDisabled"
                    :options="accessOptions"
                    :aria-label="t('profile.collaboration.shareDialog.generalAccess')"
                    trigger-class="min-w-44"
                    @update:model-value="handleChangeProfileAccess" />
            </div>
        </div>

        <HorizontalDivider v-if="!isPrivateAccessLevel" spacing="none" />

        <div v-if="!isPrivateAccessLevel">
            <div class="flex flex-col md:h-full">
                <template v-if="isOrgAccessLevel || isInviteOnlyAccessLevel">
                    <template v-if="isInviteOnlyAccessLevel">
                        <div v-if="showInvitationSkeletons" class="flex flex-col gap-4 grow">
                            <div v-for="n in 2" :key="n" class="flex items-center gap-3">
                                <SkeletonLoader class="size-10 shrink-0 rounded-full" :aria-label="t('profile.collaboration.shareDialog.loadingUser')" />
                                <div class="flex-1 flex flex-col gap-2">
                                    <SkeletonLoader class="h-3.5 w-48 rounded" :aria-label="t('profile.collaboration.shareDialog.loadingName')" />
                                    <SkeletonLoader class="h-3 w-40 rounded" :aria-label="t('profile.collaboration.shareDialog.loadingStatus')" />
                                </div>
                            </div>
                        </div>
                        <div
                            v-else-if="activeAcceptances.length === 0"
                            class="flex flex-col grow justify-center items-center text-center py-2 gap-2"
                            data-testid="no-acceptances-message">
                            <i class="bi bi-people text-2xl text-ink-muted" aria-hidden="true"></i>
                            <p class="body-small text-ink-muted">{{ t("profile.collaboration.shareDialog.noAcceptances") }}</p>
                        </div>
                        <div v-else class="grow">
                            <div class="flex flex-col gap-4">
                                <div v-for="acceptance in activeAcceptances" :key="acceptance.id" class="flex items-center gap-3">
                                    <PeopleAvatar :name="acceptance.user_name || acceptance.user_email" />
                                    <div class="min-w-0 flex-1">
                                        <div class="body-small-semibold text-ink truncate">
                                            {{ acceptance.user_name || acceptance.user_email }}
                                        </div>
                                        <I18nT keypath="profile.collaboration.shareDialog.connectedSince" tag="div" class="body-small text-ink-muted">
                                            <template #date><BaseDate :value="acceptance.accepted_at" format="relative" /></template>
                                        </I18nT>
                                    </div>
                                    <BaseDropdownMenu :sections="acceptanceMenuSections" :side-offset="8" @select="(item) => handleAcceptanceMenuSelect(item, acceptance.id)">
                                        <template #trigger>
                                            <BaseButton theme="tertiary" size="sm">
                                                {{ t("profile.collaboration.shareDialog.connectedMenu") }}
                                                <i class="bi bi-chevron-down text-xs" aria-hidden="true"></i>
                                            </BaseButton>
                                        </template>
                                    </BaseDropdownMenu>
                                </div>
                            </div>
                        </div>
                    </template>

                    <div v-if="isOrgAccessLevel">
                        <p class="body-small-bold text-ink mb-2">{{ t("profile.collaboration.shareDialog.includedMessage") }}</p>
                        <BaseCard padding="sm">
                            <p class="body-small text-ink-muted">{{ includedMessage }}</p>
                        </BaseCard>
                    </div>
                </template>
            </div>
        </div>
        <BaseNotification v-if="accessChangeError" theme="error" hide-dismiss class="mt-2" :text="accessChangeErrorMessage" />
        <BaseNotification v-if="shareLinkError" theme="error" hide-dismiss class="mt-2" :text="String(shareLinkError)" />
        <template #footer>
            <template v-if="isPrivateAccessLevel">
                <BaseButton theme="neutral" @click="handleCancelPrivate">{{ t("common.cancel") }}</BaseButton>
                <BaseButton theme="primary" :disabled="isSaveDisabled" @click="handleSaveAccess">{{ t("common.save") }}</BaseButton>
            </template>
            <template v-else-if="isOrgAccessLevel || isInviteOnlyAccessLevel">
                <BaseButton v-if="isShareBack" theme="neutral" @click="open = false">{{ t("common.cancel") }}</BaseButton>
                <BaseButton v-if="isShareBack" theme="primary" :disabled="isButtonDisabled" @click="handleShareBack">{{ t("profile.collaboration.shareDialog.share") }}</BaseButton>
                <div v-else class="grid w-full grid-cols-2 gap-3" data-testid="share-via-section">
                    <BaseButton theme="primary" class="col-span-2" :disabled="isButtonDisabled" @click="handleCopyShareLink">
                        <i class="bi" :class="isCopied ? 'bi-check-lg' : 'bi-link-45deg'" aria-hidden="true"></i>
                        {{ isCopied ? t("profile.collaboration.shareDialog.copied") : t("profile.collaboration.shareDialog.copyLink") }}
                    </BaseButton>
                    <BaseButton v-if="orgHasSlack" theme="neutral" :disabled="isButtonDisabled" @click="handleShareViaSlack">{{
                        t("profile.collaboration.shareDialog.shareViaSlack")
                    }}</BaseButton>
                    <BaseButton v-else-if="orgHasMsTeams" theme="neutral" :disabled="isButtonDisabled" @click="handleShareViaTeams">{{
                        t("profile.collaboration.shareDialog.shareViaTeams")
                    }}</BaseButton>
                    <BaseButton theme="neutral" :class="{ 'col-span-2': !hasChatShareOption }" :disabled="isButtonDisabled" @click="handleShareViaEmail">{{
                        t("profile.collaboration.shareDialog.shareViaEmail")
                    }}</BaseButton>
                </div>
            </template>
        </template>
    </BaseDialog>
    <ConfirmationDialog
        v-model:open="isReduceAccessConfirmOpen"
        :title="reduceAccessConfirmation.title"
        :text="reduceAccessConfirmation.text"
        :confirm-label="t('profile.collaboration.shareDialog.changeAccess')"
        confirm-theme="danger"
        container-class="md:w-120"
        @confirm="confirmReduceAccess"
        @cancel="cancelReduceAccess" />
</template>

<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import { useTimeoutFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { ProfileAcceptanceSummary, ShareInvitationList } from "~vue/api/generated/collab-profiles";
import type { OrganizationDetails } from "~vue/appTypes";
import { profileShareBackMessage, profileShareMessage, type ProfileShareTarget, useProfileShareActions } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { logProfileShared, type ProfileShareSource } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import OrganizationIcon from "~vue/icons/OrganizationIcon.vue";
import type { ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { ACCESS } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const { t } = useI18n();
const page = usePage<{ organization_details?: OrganizationDetails | null }>();
const orgName = computed(() => page.props.organization_details?.name ?? t("profile.collaboration.shareDialog.yourOrganization"));

type ShareProfileDialogProps = {
    existingPublicProfile: ExistingPublicProfile;
    orgHasMsTeams?: boolean;
    orgHasSlack?: boolean;
    availableAccessLevels: Set<string>;
    ownProfileUrl?: string;
    recipientName?: string;
    shareSource?: ProfileShareSource;
};

const {
    existingPublicProfile,
    orgHasMsTeams = false,
    orgHasSlack = false,
    availableAccessLevels,
    ownProfileUrl,
    recipientName,
    shareSource = "profile_page",
} = defineProps<ShareProfileDialogProps>();
const emit = defineEmits<{ shared: [target: ProfileShareTarget] }>();
const isShareBack = computed(() => shareSource === "share_back");

const profileId = computed(() => existingPublicProfile.id);

const open = defineModel<boolean>("open", { default: false });

const localAccess = ref(existingPublicProfile.access ?? ACCESS.PRIVATE);
// Access the last apply actually persisted. The private view stages the dropdown behind its
// Save button, so the selection can differ from committedAccess until Save runs.
const committedAccess = ref(existingPublicProfile.access ?? ACCESS.PRIVATE);

const accessOptions = computed(() => {
    const allOptions = [
        { key: ACCESS.PRIVATE, label: t("profile.collaboration.shareDialog.accessOptions.private") },
        { key: ACCESS.INVITED_ORG_MEMBERS, label: t("profile.collaboration.shareDialog.accessOptions.invited") },
        { key: ACCESS.ORGANIZATION, label: t("profile.collaboration.shareDialog.accessOptions.organization", { org: orgName.value }) },
    ];
    return allOptions.filter((o) => availableAccessLevels.has(o.key));
});

const hasChatShareOption = computed(() => orgHasSlack || orgHasMsTeams);

const isPrivateAccessLevel = computed(() => committedAccess.value === ACCESS.PRIVATE || localAccess.value === ACCESS.PRIVATE);
const isOrgAccessLevel = computed(() => !isPrivateAccessLevel.value && localAccess.value === ACCESS.ORGANIZATION);
const isInviteOnlyAccessLevel = computed(() => !isPrivateAccessLevel.value && localAccess.value === ACCESS.INVITED_ORG_MEMBERS);
const sharingDescription = computed(() =>
    recipientName ? t("profile.collaboration.shareDialog.descriptionWithRecipient", { name: recipientName }) : t("profile.collaboration.shareDialog.description"),
);

const statusTitle = computed(() => {
    if (isPrivateAccessLevel.value) {
        return t("profile.collaboration.shareDialog.privateTitle");
    }
    if (isOrgAccessLevel.value) {
        return t("profile.collaboration.shareDialog.organizationVisibleTitle", { org: orgName.value });
    }
    return t("profile.collaboration.sharing.invitedPeople");
});

const statusBody = computed(() => {
    if (isPrivateAccessLevel.value) {
        return recipientName ? t("profile.collaboration.sharing.privateBodyRecipient", { name: recipientName }) : t("profile.collaboration.sharing.privateBody");
    }
    if (isOrgAccessLevel.value) {
        return t("profile.collaboration.shareDialog.organizationVisibleBody");
    }
    return sharingDescription.value;
});

const invitationsUrl = computed(() => (profileId.value ? `/v1/profiles/${profileId.value}/shared-invitations` : ""));

const {
    data: invitations,
    error: invitationLoadError,
    isFetching: isFetchingAcceptances,
    execute: fetchInvitations,
} = useFetch(invitationsUrl, { immediate: false, refetch: false }).json<ShareInvitationList>();

const showInvitationSkeletons = computed(() => isFetchingAcceptances.value && invitations.value === null);
const ensureInvitationsLoaded = () => {
    if (!profileId.value || isFetchingAcceptances.value) {
        return;
    }
    if (invitations.value === null || invitationLoadError.value) {
        void fetchInvitations();
    }
};

const activeAcceptances = computed<ProfileAcceptanceSummary[]>(() => invitations.value?.acceptances?.filter((a) => a.revoked_at === null) ?? []);

const revokeUrl = ref("");
// The revoke endpoint returns 204 No Content: .json() would throw on the empty body, and useFetch
// parses before checking response.ok, so every successful revoke logged an error to Sentry.
const { execute: executeRevoke } = useFetch(revokeUrl, { immediate: false }).delete();

const acceptanceMenuSections = computed<DropdownMenuSection[]>(() => [
    { id: "access", items: [{ id: "revoke", type: "button", label: t("profile.collaboration.sharing.removeInvitation"), theme: "danger" }] },
]);

const handleAcceptanceMenuSelect = (item: DropdownMenuItem, acceptanceId: string) => {
    if (item.id === "revoke") {
        void handleRevoke(acceptanceId);
    }
};

const handleRevoke = async (acceptanceId: string) => {
    if (!profileId.value || !isInviteOnlyAccessLevel.value) {
        return;
    }
    revokeUrl.value = `/v1/profiles/${profileId.value}/acceptances/${acceptanceId}`;
    await executeRevoke();
    await fetchInvitations();
};

const invitationShareUrl = computed(() => invitations.value?.active_link?.share_url);

const { isGeneratingLink, shareLinkError, isCopied, copyLink, shareViaSlack, shareViaTeams, shareViaEmail } = useProfileShareActions({
    profileId,
    access: localAccess,
    activeShareUrl: invitationShareUrl,
    ownProfileUrl: computed(() => ownProfileUrl),
    shareSource,
});

const trackShare = (target: ProfileShareTarget) => {
    void logProfileShared({ shareSource, target, shareKind: "own_profile", access: localAccess.value });
};

const includedMessage = computed(() => (isShareBack.value ? profileShareBackMessage(recipientName ?? "") : profileShareMessage()));

const handleCopyShareLink = async () => {
    if (await copyLink(includedMessage.value)) {
        trackShare("link");
        emit("shared", "link");
    }
};

const handleShareBack = async () => {
    if (orgHasMsTeams) {
        await handleShareViaTeams();
    } else {
        await handleCopyShareLink();
    }
};

const handleShareViaSlack = async () => {
    if (await shareViaSlack()) {
        trackShare("slack");
    }
};

const handleShareViaTeams = async () => {
    if (await shareViaTeams(includedMessage.value)) {
        trackShare("teams");
        emit("shared", "teams");
    }
};

const handleShareViaEmail = async () => {
    if (await shareViaEmail()) {
        trackShare("email");
    }
};

const profileAccessPayload = ref<{ access: string } | null>(null);
const {
    execute: executeToggleAccess,
    error: accessChangeError,
    isFetching: isAccessChanging,
    statusCode: accessChangeStatus,
} = useFetch("/api/accounts/profile/", { immediate: false }).post(profileAccessPayload).json();

const accessChangeErrorMessage = computed(() =>
    accessChangeStatus.value === 429 ? t("profile.collaboration.shareDialog.accessChangeRateLimited") : t("profile.collaboration.shareDialog.accessChangeFailed"),
);

const ACCESS_ERROR_TIMEOUT_MS = 5000;
const { start: startAccessErrorTimeout, stop: stopAccessErrorTimeout } = useTimeoutFn(
    () => {
        accessChangeError.value = null;
    },
    ACCESS_ERROR_TIMEOUT_MS,
    { immediate: false },
);

watch(accessChangeError, (error) => {
    if (error) {
        startAccessErrorTimeout();
    } else {
        stopAccessErrorTimeout();
    }
});

watch(open, (isOpen) => {
    if (isOpen) {
        committedAccess.value = existingPublicProfile.access ?? ACCESS.PRIVATE;
        localAccess.value = committedAccess.value;
    }
});

watch(
    () => existingPublicProfile.access,
    (access) => {
        committedAccess.value = access ?? ACCESS.PRIVATE;
    },
);

watch([open, isInviteOnlyAccessLevel], ([isOpen, isInviteOnly]) => {
    if (isOpen && isInviteOnly) {
        ensureInvitationsLoaded();
    } else if (!isOpen) {
        invitations.value = null;
        invitationLoadError.value = null;
        accessChangeError.value = null;
    }
});

const ACCESS_RANK: Record<string, number> = {
    [ACCESS.PRIVATE]: 0,
    [ACCESS.INVITED_ORG_MEMBERS]: 1,
    [ACCESS.ORGANIZATION]: 2,
};

const isReduceAccessConfirmOpen = ref(false);
const pendingAccess = ref<string | null>(null);

const reduceAccessConfirmation = computed(() =>
    pendingAccess.value === ACCESS.PRIVATE
        ? {
              title: t("profile.collaboration.sharing.confirmPrivateTitle"),
              text: t("profile.collaboration.sharing.confirmPrivate"),
          }
        : {
              title: t("profile.collaboration.sharing.confirmInvitedTitle"),
              text: t("profile.collaboration.sharing.confirmInvited"),
          },
);

function restoreSavedAccess(): void {
    localAccess.value = committedAccess.value;
}

function cancelReduceAccess(): void {
    pendingAccess.value = null;
    restoreSavedAccess();
}

// Dismissing via Escape or the overlay closes the dialog without emitting cancel.
watch(isReduceAccessConfirmOpen, (isOpen) => {
    if (!isOpen && pendingAccess.value !== null) {
        cancelReduceAccess();
    }
});

async function confirmReduceAccess(): Promise<void> {
    const newShared = pendingAccess.value;
    pendingAccess.value = null;
    if (newShared) {
        await applyProfileAccess(newShared);
    }
}

function isReducingAccess(newShared: string): boolean {
    return (ACCESS_RANK[newShared] ?? 0) < (ACCESS_RANK[committedAccess.value] ?? 0);
}

async function handleChangeProfileAccess(newShared: string, forceApply = false): Promise<void> {
    if (!existingPublicProfile || committedAccess.value === newShared) {
        return;
    }
    if (isReducingAccess(newShared)) {
        pendingAccess.value = newShared;
        isReduceAccessConfirmOpen.value = true;
        return;
    }
    if (!forceApply && committedAccess.value === ACCESS.PRIVATE) {
        // Private profile: hold the selection until the Save button commits it.
        return;
    }
    await applyProfileAccess(newShared);
}

const isSaveDisabled = computed(() => isButtonDisabled.value || localAccess.value === committedAccess.value);

function handleSaveAccess(): void {
    void handleChangeProfileAccess(localAccess.value, true);
}

function handleCancelPrivate(): void {
    restoreSavedAccess();
    open.value = false;
}

async function applyProfileAccess(newShared: string): Promise<void> {
    accessChangeError.value = null;

    profileAccessPayload.value = { access: newShared };

    await executeToggleAccess();

    if (accessChangeError.value) {
        restoreSavedAccess();
    } else {
        committedAccess.value = newShared;
        void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_VISIBILITY_CHANGED, {
            new_access: newShared,
            is_shared: newShared === ACCESS.ORGANIZATION,
        });

        router.reload({ only: ["existing_public_profile", "viewer_public_profile", "available_access_levels", "share_back_enabled"] });
    }
}

const isButtonDisabled = computed(() => isGeneratingLink.value || isAccessChanging.value);
</script>
